import type { Dispatch } from 'redux'
import { setBulkActionProgress, setRecipeProgress } from '../actions/progress'
import type { BulkActionState } from '../types/reducers/bulk_actions'
import type { CardsState } from '../types/reducers/cards'
import type { ProgressState } from '../types/reducers/progress'
import type { UserState } from '../types/reducers/user'
import { Toasts } from '../components/toaster/Toast'
import { axiosGet } from '../util/api'
import {
    observeBulkActionProgress,
    pollPendingBulkActionRuns,
} from '../components/ui/bulkActionExecution'
import { BulkActionInitialState, bulkActionReducer } from './bulk_actions'
import { CardsInitialState, cardsReducer } from './cards'
import { ProgressInitialState, progressReducer } from './progress'
import { SmartBidSettingsInitialState, smartBidSettingsReducer } from './smart_bid_settings'
import { UserInitialState, userReducer } from './user'

import type { SmartBidSettingsState } from '../constants/reducers/smart_bid_settings'
import type { Action } from '../constants/reducers/state'

export interface State {
    user: UserState
    cards: CardsState
    smartBidSettings: SmartBidSettingsState
    progress: ProgressState
    bulkActions: BulkActionState
}

const InitialState: State = {
    user: UserInitialState,
    cards: CardsInitialState,
    smartBidSettings: SmartBidSettingsInitialState,
    progress: ProgressInitialState,
    bulkActions: BulkActionInitialState,
}

export const rootReducer = (state: State = InitialState, action: Action) => {
    const user = userReducer(state.user, action)
    currentSelection()
    return {
        user,
        cards: cardsReducer(state.cards, action),
        smartBidSettings: smartBidSettingsReducer(state.smartBidSettings, action),
        progress: progressReducer(state.progress, action),
        bulkActions: bulkActionReducer(state.bulkActions, action),
    }
}

// Both polls go to ts-bulk-actions, which answers 400 to a request with no ProfileId header.
// The axios default sets that header only when the account has a managed own profile
// (reducers/user.ts), so a poll sends no request with no header.
const profileHeader = () => window.axios?.defaults?.headers?.common?.['ProfileId']

type PollState = { inFlight: boolean; unauthorized: number; nextRequestAt: number }

// All poll state of one selection. Each change of the ProfileId header value, the change to
// "no header" included, makes a new object. The object identity is the generation: an answer
// to a request of an earlier object is ignored, also when the profile value is the same again.
type PollSelection = {
    header: unknown
    stopped: boolean
    toastShown: boolean
    polls: Record<string, PollState>
}

const newSelection = (header: unknown): PollSelection => ({
    header,
    stopped: false,
    toastShown: false,
    polls: {},
})
let selection = newSelection(undefined)

// The root reducer calls this after each action, because the user reducer writes the header.
// A tick calls it too. A logout and a login with no tick between them is thus a change.
const currentSelection = () => {
    const header = profileHeader()
    if (String(header) !== String(selection.header)) {
        selection = newSelection(header)
    }
    return selection
}

// The proxy gives these while ts-bulk-actions reloads. The next tick tries again.
const RELOAD_STATUSES = [502, 503, 504]
const UNAUTHORIZED_TOAST_AT = 3
const UNAUTHORIZED_BACKOFF_MAX_S = 60
// util/api sets no client timeout. This one frees the in-flight slot of a request with no answer.
export const POLL_TIMEOUT_MS = 10_000

const showStopToast = (sent: PollSelection) => {
    if (sent.toastShown) {
        return
    }
    sent.toastShown = true
    Toasts.error(
        'Run progress is not available. Reload the page to try again.',
        'progress-poll-stopped',
    )
}

// Gives null with no header (the caller reads that as an empty map) and a rejection for a
// tick that sends no request or gets no usable answer (the caller keeps the stored progress).
const pollProgress = (endpoint: string): Promise<any> | null => {
    const sent = currentSelection()
    if (!sent.header) {
        return null
    }
    const poll = (sent.polls[endpoint] ??= { inFlight: false, unauthorized: 0, nextRequestAt: 0 })
    // A 400 or 403 does not change on the next tick: both polls pass the same admission, so
    // one refusal stops both for this selection. One request in flight for each poll keeps a
    // slow ts-bulk-actions free of a queue of polls.
    if (sent.stopped || poll.inFlight || Date.now() < poll.nextRequestAt) {
        return Promise.reject()
    }

    poll.inFlight = true
    // `ignore` turns off the toast of the shared handler: it shows one for each tick.
    return axiosGet(endpoint, null, true, { ignore: true, timeout: POLL_TIMEOUT_MS }).then(
        (res) => {
            poll.inFlight = false
            if (sent !== currentSelection()) {
                throw new Error('progress poll answer for an earlier selection')
            }
            poll.unauthorized = 0
            poll.nextRequestAt = 0
            sent.toastShown = false
            return res
        },
        (err) => {
            poll.inFlight = false
            if (sent !== currentSelection()) {
                throw err
            }
            const status = err?.response?.status
            if (status && status !== 401) {
                // Another answer ends the run of consecutive 401 answers.
                poll.unauthorized = 0
                poll.nextRequestAt = 0
            }
            if (status === 401) {
                // A session can give one bad answer, so a 401 slows the poll and does not
                // stop it: 2 s, 4 s, 8 s ... 60 s between requests until a 200.
                poll.unauthorized += 1
                const waitSeconds = Math.min(UNAUTHORIZED_BACKOFF_MAX_S, 2 ** poll.unauthorized)
                poll.nextRequestAt = Date.now() + waitSeconds * 1000
                if (poll.unauthorized === UNAUTHORIZED_TOAST_AT) {
                    showStopToast(sent)
                }
            } else if (status === 400 || status === 403) {
                sent.stopped = true
                showStopToast(sent)
            } else if (err?.response && !RELOAD_STATUSES.includes(status)) {
                Toasts.error('Something went wrong', 'progress-poll-error')
            }
            throw err
        },
    )
}

export function fetchRecipeProgress() {
    return (dispatch: Dispatch<any>, getState: () => State) => {
        const request = pollProgress('api/v5/recipe-progress/info') ?? Promise.resolve({ data: [] })
        return request
            .then((res) => {
                const state: State = getState()
                const finishedUuids = []

                if (Object.keys(state.progress.recipes).length > 0 && res.data.length == 0) {
                    // Find the item that previously existed, but does not exist anymore
                    const finishedId = Object.keys(state.progress.recipes).filter(
                        (k) => !res.data[k],
                    )[0]
                    const finished = state.progress.recipes[parseInt(finishedId)]

                    // Then pull the response data for that item
                    if (finished?.uuid) {
                        finishedUuids.push(finished.uuid)
                    }
                }

                dispatch(setRecipeProgress(res.data))
                return finishedUuids
            })
            .catch(() => {
                return []
            })
    }
}

export function fetchBulkActionProgress() {
    return (dispatch: Dispatch<any>, getState: () => State) => {
        const request =
            pollProgress('api/v5/bulk-action-progress/info') ?? Promise.resolve({ data: {} })
        return request
            .then((res) => {
                const state: State = getState()
                // Empty objects seem to get converted into arrays, instead of objects
                if (Array.isArray(res.data) && res.data.length == 0) {
                    res.data = {}
                }

                const finishedUuids = findFinishedBulkActionUuids(
                    state.progress.bulk_actions,
                    res.data,
                )

                observeBulkActionProgress(res.data)
                dispatch(setBulkActionProgress(res.data))
                pollPendingBulkActionRuns()
                return finishedUuids
            })
            .catch(() => {
                // The result of a run comes from Laravel, so it does not wait for this poll.
                pollPendingBulkActionRuns()
                return []
            })
    }
}

type BulkActionProgressEntry = { uuid?: string; extra?: { uuid?: string } }
type BulkActionProgressEntries = Record<string, BulkActionProgressEntry>
export type FinishedBulkActionRun = { uuid: string; progressClass: string }

const progressUuid = (entry?: BulkActionProgressEntry) => entry?.uuid ?? entry?.extra?.uuid

/** Returns every run that disappeared, even while unrelated runs remain active. */
export const findFinishedBulkActionUuids = (
    previous: BulkActionProgressEntries,
    current: BulkActionProgressEntries,
) => {
    const currentUuids = new Set(
        Object.values(current ?? {})
            .map(progressUuid)
            .filter(Boolean),
    )

    return Object.entries(previous ?? {})
        .map(([progressClass, entry]) => ({ progressClass, uuid: progressUuid(entry) }))
        .filter(
            (run): run is FinishedBulkActionRun => Boolean(run.uuid) && !currentUuids.has(run.uuid),
        )
}
